import axios, { AxiosInstance, AxiosRequestConfig as OriginalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_CONFIG, HTTP_HEADERS } from '@/constants/apiConfig';
import { corsConfig, devCorsConfig } from '@/constants/corsConfig';

/**
 * Extended Axios request configuration with custom properties
 * @interface CustomAxiosRequestConfig
 * @extends OriginalAxiosRequestConfig
 * @property {number} [retryCount] - Number of retry attempts made for the request
 * @property {boolean} [noCache] - If true, bypasses the cache for this request
 */
interface CustomAxiosRequestConfig extends OriginalAxiosRequestConfig {
  retryCount?: number;
  noCache?: boolean;
}

/**
 * Cache item structure for in-memory caching
 * @interface CacheItem
 * @template T - Type of the cached data
 * @property {T} data - The cached data
 * @property {number} timestamp - Timestamp when the data was cached
 */
interface CacheItem<T> {
  data: T;
  timestamp: number;
}

/**
 * Cache time-to-live in milliseconds (5 minutes)
 * @constant {number}
 */
const CACHE_TTL = 5 * 60 * 1000;

/**
 * In-memory cache store for API responses
 * @type {Map<string, CacheItem<any>>}
 */
const requestCache = new Map<string, CacheItem<any>>();

/**
 * Base API Service Class
 * 
 * Provides common functionality for all API services including:
 * - Request/response interceptors
 * - Retry mechanism with exponential backoff
 * - Response caching
 * - CORS handling with fallback
 * - Error handling and logging
 * 
 * @class BaseApiService
 * @example
 * class MyApiService extends BaseApiService {
 *   async getData() {
 *     return this.get<MyDataType>('/api/endpoint');
 *   }
 * }
 */
export class BaseApiService {
  /**
   * Axios instance with custom configuration
   * @protected
   * @type {AxiosInstance}
   */
  protected axiosInstance: AxiosInstance;
  private readonly cache: Map<string, CacheItem<any>> = new Map();
  private readonly CACHE_TTL: number = 5 * 60 * 1000; // 5 minutes

  /**
   * Creates an instance of BaseApiService
   * Initializes the Axios instance with default configuration and sets up interceptors
   */
  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: HTTP_HEADERS.JSON,
    });

    this.setupInterceptors();
  }

  /**
   * Sets up request and response interceptors for the Axios instance
   * @private
   * @returns {void}
   * @description
   * - Request Interceptor:
   *   - Handles request caching
   *   - Adds request logging
   * - Response Interceptor:
   *   - Implements response caching for GET requests
   *   - Handles retries with exponential backoff
   *   - Provides detailed error logging
   */
  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const method = config.method?.toUpperCase() || 'GET';
        const cacheKey = this.generateCacheKey(config);
        
        // Skip cache for non-GET requests or if explicitly disabled
        if (method === 'GET' && !(config as CustomAxiosRequestConfig).noCache) {
          const cached = requestCache.get(cacheKey);
          if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
            console.log(`💾 Using cached response for: ${config.url}`);
            return {
              ...config,
              adapter: () => Promise.resolve({
                data: cached.data,
                status: 200,
                statusText: 'OK',
                headers: { 'x-cache': 'HIT' },
                config,
              }),
            };
          }
        }

        console.log(`📡 Making ${method} request to: ${config.url}`);
        return config;
      },
      (error) => {
        console.error('❌ Request error:', error);
        // Ensure we always reject with an Error object
        const rejectionError = error instanceof Error ? error : new Error(String(error));
        return Promise.reject(rejectionError);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        const method = response.config.method?.toUpperCase() || 'GET';
        const cacheKey = this.generateCacheKey(response.config);
        
        // Cache successful GET responses
        if (method === 'GET' && response.status === 200 && !(response.config as CustomAxiosRequestConfig).noCache) {
          requestCache.set(cacheKey, {
            data: response.data,
            timestamp: Date.now()
          });
        }

        console.log(`✅ Response received from ${response.config.url}:`, response.status);
        return response;
      },
      async (error: AxiosError) => {
        const config = error.config as any;
        
        // If we don't have a config or retry count, reject
        if (!config?.retryCount) {
          console.error(`❌ Final attempt failed for ${config?.url}:`, error.message);
          return Promise.reject(error);
        }

        // Check if we should retry
        if (config.retryCount < API_CONFIG.MAX_RETRIES) {
          // Calculate delay with exponential backoff
          const delay = Math.min(
            API_CONFIG.RETRY_DELAY * Math.pow(2, config.retryCount - 1),
            30000 // Max 30 seconds
          );

          console.log(`🔄 Retrying ${config.url} (attempt ${config.retryCount + 1}/${API_CONFIG.MAX_RETRIES}) in ${delay}ms`);
          
          // Wait for the delay
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Increment retry count and retry
          config.retryCount++;
          return this.axiosInstance.request(config);
        }

        const errorMessage = `Max retries (${API_CONFIG.MAX_RETRIES}) exceeded for ${config.url}`;
        console.error(`❌ ${errorMessage}`);
        const maxRetriesError = new Error(errorMessage);
        if (error instanceof Error) {
          maxRetriesError.stack = error.stack;
          maxRetriesError.name = error.name;
        }
        return Promise.reject(maxRetriesError);
      }
    );
  }

  /**
   * Makes an HTTP request with CORS fallback handling
   * @template T - Expected response data type
   * @param {CustomAxiosRequestConfig} config - The request configuration
   * @param {boolean} [useDevCors=false] - Whether to use development CORS settings
   * @returns {Promise<AxiosResponse<T>>} The API response
   * @throws {Error} If the request fails after all retries and fallbacks
   * @description
   * - Attempts the request with the primary CORS configuration
   * - Falls back to alternative CORS settings if the first attempt fails
   * - Includes detailed logging for debugging
   */
  protected async makeRequest<T>(
    config: CustomAxiosRequestConfig,
    useDevCors: boolean = false
  ): Promise<AxiosResponse<T>> {
    try {
      // Try with dev CORS config first (more permissive)
      if (useDevCors) {
        const response = await this.axiosInstance.request({
          ...config,
          ...devCorsConfig,
        });
        console.log('✅ Success with dev CORS config');
        return response;
      }

      // Standard request
      const response = await this.axiosInstance.request({
        ...config,
        ...corsConfig,
      });
      console.log('✅ Success with standard CORS config');
      return response;
    } catch (error: unknown) {
      const errorObj = error as Error & {
        response?: any;
        code?: string;
        message?: string;
      };
      
      console.error('⚠️ Primary request failed:', this.sanitizeError(errorObj));
      
      // Only attempt fallback for network-related errors or CORS issues
      const isNetworkError = !errorObj.response;
      const isCorsError = errorObj.code === 'ERR_NETWORK' || 
                         errorObj.message?.includes('Network Error') ||
                         errorObj.message?.includes('CORS');
      
      if (!isNetworkError && !isCorsError) {
        // If it's not a network/CORS error, rethrow the original error
        throw this.enhanceError(errorObj);
      }
      
      console.log('⚠️ Primary config failed, trying fallback CORS config...');
      
      // Fallback to alternative config
      try {
        const response = await this.axiosInstance.request({
          ...config,
          ...(useDevCors ? corsConfig : devCorsConfig),
          // Add timeout for the fallback attempt
          timeout: config.timeout || API_CONFIG.TIMEOUT,
        });
        console.log('✅ Success with fallback CORS config');
        return response;
      } catch (fallbackError) {
        console.error('❌ Request failed with both configs');
        // Ensure we have an Error object
        const finalError = fallbackError instanceof Error ? fallbackError : new Error(String(fallbackError));
        return Promise.reject(finalError);
      }
    }
  }

  /**
   * Performs a GET request with built-in retry and caching
   * @template T - Expected response data type
   * @param {string} url - The endpoint URL (relative to baseURL)
   * @param {Record<string, any>} [params={}] - Query parameters
   * @param {boolean} [useDevCors=false] - Whether to use development CORS settings
   * @param {Object} [options] - Additional options
   * @param {boolean} [options.noCache=false] - If true, bypasses the cache
   * @returns {Promise<AxiosResponse<T>>} The API response
   * @description
   * - Implements request caching with TTL
   - Supports retry with exponential backoff
   - Handles CORS configuration
   - Includes cache-busting for noCache option
   */
  protected async get<T>(
    url: string,
    params: Record<string, any> = {},
    useDevCors: boolean = false,
    options: { noCache?: boolean } = {}
  ): Promise<AxiosResponse<T>> {
    const config: CustomAxiosRequestConfig = {
      method: 'GET',
      url,
      params: { ...(options.noCache ? { _: Date.now() } : {}), ...params },
      retryCount: 1, // Initialize retry counter
      noCache: options.noCache,
      // Apply CORS headers directly to the headers object
      ...(useDevCors ? { headers: devCorsConfig.headers } : {})
    };

    try {
      const response = await this.axiosInstance.request<T>(config);
      return response;
    } catch (error) {
      console.error(`GET request to ${url} failed:`, error);
      throw error;
    }
  }

  /**
   * Performs a POST request with retry capability
   * @template T - Expected response data type
   * @param {string} url - The endpoint URL (relative to baseURL)
   * @param {any} [data={}] - The request payload
   * @param {boolean} [useDevCors=false] - Whether to use development CORS settings
   * @param {Object} [options] - Additional options
   * @param {boolean} [options.noRetry=false] - If true, disables retry mechanism
   * @returns {Promise<AxiosResponse<T>>} The API response
   * @description
   * - Implements retry with exponential backoff (configurable)
   - Handles CORS configuration
   - Includes request/response logging
   */
  protected async post<T>(
    url: string,
    data: any = {},
    useDevCors: boolean = false,
    options: { noRetry?: boolean } = {}
  ): Promise<AxiosResponse<T>> {
    const config: CustomAxiosRequestConfig = {
      method: 'POST',
      url,
      data,
      retryCount: 1, // Initialize retry counter
      // Apply CORS headers directly to the headers object
      ...(useDevCors ? { headers: devCorsConfig.headers } : {})
    };

    try {
      const response = await this.axiosInstance.request<T>(config);
      return response;
    } catch (error) {
      console.error(`POST request to ${url} failed:`, error);
      throw error;
    }
  }

  /**
   * Generates a unique cache key for a request based on its configuration
   * @private
   * @param {CustomAxiosRequestConfig} config - The request configuration
   * @returns {string} A unique cache key
   * @description
   * The cache key is generated using:
   * - HTTP method
   * - URL
   * - Sorted query parameters
   * - Request body (for non-GET requests)
   */
  private generateCacheKey(config: CustomAxiosRequestConfig): string {
    const { url, method, params, data } = config;
    return `${method}:${url}:${JSON.stringify(params || {})}:${JSON.stringify(data || {})}`;
  }

  /**
   * Clear the request cache
   */
  public clearCache(): void {
    this.cache.clear();
    console.log('🧹 Cleared API request cache');
  }

  /**
   * Sanitizes error objects to safely extract relevant information
   * @private
   * @param {Error & { response?: any; code?: string; config?: any }} error - The error object to sanitize
   * @returns {object} Sanitized error information
   */
  private sanitizeError(error: Error & { response?: any; code?: string; config?: any }): Record<string, unknown> {
    if (!error) return { message: 'Unknown error' };
    
    // For Axios errors, extract relevant information
    const axiosError = error as any; // Type assertion for Axios error
    if (axiosError.isAxiosError) {
      return {
        message: axiosError.message,
        code: axiosError.code,
        status: axiosError.response?.status,
        statusText: axiosError.response?.statusText,
        url: axiosError.config?.url,
        method: error.config?.method,
      };
    }
    
    // For standard errors, just return the message and stack
    return {
      message: error.message,
      stack: process.env.NODE_ENV === 'development' ? error.stack : undefined,
    };
  }

  /**
   * Enhances an error with additional context
   * @private
   * @param {Error & { response?: any; code?: string; config?: any }} error - The original error
   * @returns {Error} Enhanced error with additional context
   */
  private enhanceError(error: Error & { response?: any; code?: string; config?: any }): Error {
    const axiosError = error as any; // Type assertion for Axios error
    if (axiosError.isAxiosError) {
      const { message, response } = axiosError;
      const enhancedError = new Error(message) as Error & {
        isAxiosError: boolean;
        config: any;
        code?: string;
        response?: any;
        isNetworkError: boolean;
        status?: number;
        statusText?: string;
        data?: any;
      };
      
      enhancedError.isAxiosError = true;
      enhancedError.config = axiosError.config;
      enhancedError.code = axiosError.code;
      enhancedError.response = response;
      enhancedError.isNetworkError = !response;
      
      if (response) {
        enhancedError.status = response.status;
        enhancedError.statusText = response.statusText;
        enhancedError.data = response.data;
      }
      
      return enhancedError;
    }
    
    return error;
  }

  /**
   * PUT request
   */
  protected async put<T>(url: string, data?: any, config?: CustomAxiosRequestConfig, useDevCors: boolean = false): Promise<AxiosResponse<T>> {
    return this.makeRequest<T>({ ...config, method: 'PUT', url, data }, useDevCors);
  }

  /**
   * DELETE request
   */
  protected async delete<T>(url: string, config?: CustomAxiosRequestConfig, useDevCors: boolean = false): Promise<AxiosResponse<T>> {
    return this.makeRequest<T>({ ...config, method: 'DELETE', url }, useDevCors);
  }
}
